import { ICarePlan, isCarePlan } from '../modules/unsw-fhir/CarePlan';
import {
  IOperationOutcome,
  isOperationOutcome,
} from '../modules/unsw-fhir/OperationOutcome';
import { FhirInstance } from '../config/FhirClient';
import { IBundle, IBundleEntry } from '../modules/unsw-fhir/Bundle';
import { IQuestionnaire } from '../modules/unsw-fhir/Questionnaire';
import { ICondition } from '../modules/unsw-fhir/Condition';
import produce from 'immer';
import { isReferenceEqual } from '../modules/unsw-fhir/QuestionnaireResponse/comparator';
import {
  createQuestionnaireReference,
  getReferenceId,
  createConditionReference,
  createQuestionnaireResponseReference,
} from '../modules/unsw-fhir/Reference';
import { createActivity } from '../modules/unsw-care-plan';
import { getConditionByQuestionnaire } from '../modules/unsw-care-plan/eolCarePlan';
import { getNextLinkStr } from '../selectors/bundle';

export const getCarePlanByPatientId = async (
  patientId: string,
): Promise<ICarePlan | undefined> => {
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/CarePlan',
      { params: { subject: patientId } },
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return undefined;
    }

    return data.entry
      .filter(entry => {
        return !!entry.resource && isCarePlan(entry.resource);
      })
      .map((entry): ICarePlan => entry.resource as ICarePlan)[0];
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const getCarePlanById = async (
  id: string,
): Promise<ICarePlan | undefined> => {
  try {
    const response = await FhirInstance.get<ICarePlan | IOperationOutcome>(
      `/CarePlan/${id}`,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const postCarePlan = async (carePlan: ICarePlan) => {
  try {
    const response = await FhirInstance.post<ICarePlan | IOperationOutcome>(
      '/CarePlan',
      carePlan,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const putCarePlan = async (carePlanId: string, carePlan: ICarePlan) => {
  try {
    const response = await FhirInstance.put<ICarePlan | IOperationOutcome>(
      `/CarePlan/${carePlanId}`,
      carePlan,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};

export const updateCarePlan = async (
  carePlan: ICarePlan,
  questionnaires: IQuestionnaire[],
  conditions: ICondition[],
): Promise<ICarePlan | undefined> => {
  const modified = produce(carePlan, draft => {
    if (!draft.activity) return carePlan;

    questionnaires.forEach(questionnaire => {
      if (!draft.activity) return;
      const index = draft.activity.findIndex(
        activity =>
          activity.detail &&
          getReferenceId(activity.detail.definition) === questionnaire.id,
      );
      if (index < 0) {
        const condition = getConditionByQuestionnaire(
          questionnaire,
          conditions,
        );
        draft.activity.push(
          createActivity({
            definition: createQuestionnaireReference(questionnaire.id!),
            reasonReference:
              condition && createConditionReference(condition.id!),
            status: 'in-progress',
          }),
        );
      } else {
        if (
          draft.activity[index].detail !== undefined &&
          draft.activity[index].detail!.status === 'cancelled'
        ) {
          draft.activity[index].detail!.status = 'in-progress';
        }
      }
    });

    // cancel an activity if it's been removed
    draft.activity.forEach(activity => {
      if (!activity.detail || !activity.detail.definition) return;
      const index = questionnaires.findIndex(
        q => getReferenceId(activity.detail!.definition) === q.id!,
      );
      if (index < 0) {
        activity.detail.status = 'cancelled';
      }
    });
  });
  return await putCarePlan(modified.id!, modified);
};

export const completeCarePlanActivity = async (
  carePlan: ICarePlan,
  questionnaireId: string,
  questionnaireResponseId: string,
): Promise<ICarePlan | undefined> => {
  if (!carePlan.activity) return;
  const index = carePlan.activity.findIndex(
    activity =>
      activity.detail &&
      getReferenceId(activity.detail.definition) === questionnaireId &&
      activity.detail.status === 'in-progress',
  );
  if (index < 0) return;
  const modified = produce(carePlan, draft => {
    if (draft.activity![index].detail) {
      draft.activity![index].detail!.status = 'completed';
      const outcomeReference = createQuestionnaireResponseReference(
        questionnaireResponseId,
      );
      if (!draft.activity![index].outcomeReference)
        draft.activity![index].outcomeReference = [];

      const outcomeReferenceIndex =
        (draft.activity![index].outcomeReference &&
          draft.activity![index].outcomeReference!.findIndex(outcome =>
            isReferenceEqual(outcome, outcomeReference),
          )) ||
        -1;

      if (outcomeReferenceIndex < 0) {
        draft.activity![index].outcomeReference!.push(outcomeReference);
      }
    }
  });

  return await putCarePlan(modified.id!, modified);
};

const getCarePlanByLink = async (link: string) => {
  const response = await FhirInstance.get<IBundle | IOperationOutcome>(link);
  const { data } = response;
  if (isOperationOutcome(data) || !data.entry) {
    return undefined;
  }
  return data;
};

const handlePagnation = async (link: string) => {
  let next: string | undefined = link;
  const bundles: IBundle[] = [];
  do {
    const bundle = await getCarePlanByLink(next);
    if (!bundle || isOperationOutcome(bundle)) {
      return [];
    }
    bundles.push(bundle);

    next = getNextLinkStr(bundle);
  } while (!!next);

  return bundles;
};

export const getAllCarePlans = async () => {
  try {
    const response = await FhirInstance.get<IBundle | IOperationOutcome>(
      '/CarePlan',
    );
    const { data } = response;
    if (isOperationOutcome(data) || !data.entry) {
      return [];
    }
    const next = getNextLinkStr(data);
    if (next) {
      const bundles = [data, ...(await handlePagnation(next))];
      const carePlans = bundles.map(bundle =>
        bundle
          .entry!.filter(
            (entry: IBundleEntry) =>
              !!entry.resource && isCarePlan(entry.resource),
          )
          .map((entry): ICarePlan => entry!.resource as ICarePlan),
      );
      return carePlans.flat();
    }
    return data.entry
      .filter(entry => {
        return !!entry.resource && isCarePlan(entry.resource);
      })
      .map((entry): ICarePlan => entry.resource as ICarePlan);
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const deleteCarePlanById = async (
  id: string,
): Promise<ICarePlan | undefined> => {
  try {
    const response = await FhirInstance.delete<ICarePlan | IOperationOutcome>(
      `/CarePlan/${id}`,
    );
    const { data } = response;
    if (isOperationOutcome(data)) {
      return undefined;
    }
    return data;
  } catch (e) {
    console.error(e);
    return undefined;
  }
};
